import React, { useState } from 'react';

const FAQ: React.FC = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqItems = [
    {
      question: "What is MarketMate?",
      answer: "MarketMate is a platform that connects customers with local farmers, enabling fresh produce delivery directly from farm to your table. We also empower farmers to sell their products and riders to earn by delivering orders."
    },
    {
      question: "How do I order fresh produce?",
      answer: "Simply download our app or visit our website, browse available products from local farmers, add items to your cart, and checkout. We'll deliver your fresh produce within 24-48 hours."
    },
    {
      question: "What payment methods are accepted?",
      answer: "We accept all major credit cards, debit cards, mobile money, and bank transfers. Choose the payment method that works best for you at checkout."
    },
    {
      question: "How fast is delivery?",
      answer: "Most orders are delivered within 24-48 hours depending on your location. Express delivery is available in select areas. Track your order in real-time through our app."
    },
    {
      question: "Where do the products come from?",
      answer: "All products come directly from verified local farmers in your area. We ensure quality and freshness by maintaining direct relationships with our farmer partners."
    },
    {
      question: "How do I join as a vendor or rider?",
      answer: "Farmers and riders can join through our platform by completing a simple registration process. Visit the 'Join Us' section to apply and start earning with MarketMate."
    }
  ];

  return (
    <div className="w-full overflow-hidden pt-24">
      {/* FAQ Hero Section */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#f0faf7] to-[#f8fcfb] py-20 md:py-32 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mb-6">
              <span className="text-emerald-700">Frequently Asked</span>
            </h1>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-orange-500 mb-6">
              Questions
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Everything you need to know about MarketMate
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content Section */}
      <section className="relative w-full overflow-hidden bg-cover bg-center bg-no-repeat py-24" style={{ backgroundImage: 'url(/images/Homesec6bg.png)' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-green-600/85 via-green-600/80 to-green-700/85"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div
                key={index}
                className="bg-white/90 rounded-2xl overflow-hidden shadow-lg shadow-slate-900/20 hover:shadow-xl transition-shadow"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-5 flex items-center justify-between text-left hover:bg-white/95 transition-colors"
                >
                  <span className="text-base sm:text-lg font-semibold text-slate-900 pr-4">
                    {item.question}
                  </span>
                  <svg
                    className={`w-6 h-6 text-emerald-700 flex-shrink-0 transition-transform ${
                      expandedFAQ === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                </button>

                {expandedFAQ === index && (
                  <div className="px-6 sm:px-8 pb-5 border-t border-slate-200">
                    <p className="text-slate-700 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Two - Join the Team */}
      <section className="relative w-full bg-gradient-to-b from-[#e8f5f0] via-[#e8f5f0] to-[#f0faf7] overflow-hidden">
        {/* Image Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-12">
          <div className="flex justify-center">
            <div className="w-full max-w-5xl">
              <img
                src="/images/Aboutsec4.png"
                alt="Join MarketMate Team - Vendor, Customer, Rider"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* CTA Card Section */}
        <div className="flex justify-center pb-20 md:pb-32 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-2xl bg-orange-500 rounded-3xl p-8 sm:p-12 text-center shadow-2xl shadow-orange-500/30">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6">
              Join the MarketMate Team
            </h2>
            <p className="text-base sm:text-lg text-white/95 leading-relaxed mb-10">
              Help us make fresh food more accessible to everyone. If you're passionate about building, solving real problems, and creating impact, we'd love to have you on the journey.
            </p>
            <button className="inline-block bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 sm:py-4 px-8 sm:px-10 rounded-full transition-colors text-base sm:text-lg shadow-lg">
              View Open Roles <span className="ml-2">→</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
